import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'react-hot-toast';
import { 
  ClipboardList, ArrowLeft, CheckCircle2, Loader2, Package, 
  MapPin, User, Clock, AlertTriangle, Check, X, Play
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PickListView = () => {
  const navigate = useNavigate();
  const { pickListNumber } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pickList, setPickList] = useState(null);
  const [pickConfirmations, setPickConfirmations] = useState({});
  const [isPickingMode, setIsPickingMode] = useState(false);


  const loadPickList = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/warehouse/pick-lists/${pickListNumber}`);
      setPickList(response.data);
      
      // Initialize pick confirmations
      const confirmations = {};
      (response.data.items || []).forEach(item => {
        confirmations[item.item_number] = {
          picked_quantity: item.picked_quantity || 0,
          variance_reason: item.variance_reason || ''
        };
      });
      setPickConfirmations(confirmations);
    } catch (error) {
      console.error('Error loading pick list:', error);
      toast.error('Failed to load pick list');
      navigate('/warehouse/pick-lists');
    } finally {
      setIsLoading(false);
    }
  }, [pickListNumber, navigate]);

  useEffect(() => {
    loadPickList();
  }, [loadPickList]);

  const handleQuantityChange = (itemNumber, quantity) => {
    setPickConfirmations(prev => ({
      ...prev,
      [itemNumber]: {
        ...prev[itemNumber],
        picked_quantity: parseFloat(quantity) || 0
      }
    }));
  };

  const handleVarianceChange = (itemNumber, reason) => {
    setPickConfirmations(prev => ({
      ...prev,
      [itemNumber]: {
        ...prev[itemNumber],
        variance_reason: reason
      }
    }));
  };

  const handleConfirmPicking = async () => {
    const confirmations = pickList.items
      .filter(item => item.status !== 'PICKED')
      .map(item => ({
        item_number: item.item_number,
        picked_quantity: pickConfirmations[item.item_number]?.picked_quantity || 0,
        variance_reason: pickConfirmations[item.item_number]?.variance_reason || null,
        picked_by: 'PICKER'
      }));

    if (confirmations.length === 0) {
      toast.error('No items to confirm');
      return;
    }

    // Validate that all items have quantities
    const invalidItems = confirmations.filter(c => c.picked_quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error('Please enter picked quantities for all items');
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/warehouse/pick-lists/${pickListNumber}/confirm`,
        confirmations
      );

      toast.success('Picking confirmed successfully!');
      setPickList(response.data);
      setIsPickingMode(false);

      // If fully picked, offer to proceed to packing
      if (response.data.status === 'PICKED') {
        toast.success('Pick List completed! Ready for packing.', { duration: 4000 });
      }
    } catch (error) {
      console.error('Error confirming picks:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm picking');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'NOT_STARTED': 'bg-gray-100 text-gray-800',
      'IN_PROGRESS': 'bg-yellow-100 text-yellow-800',
      'PARTIALLY_PICKED': 'bg-orange-100 text-orange-800',
      'PICKED': 'bg-green-100 text-green-800',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getItemStatusColor = (status) => {
    const colors = {
      'NOT_STARTED': 'text-gray-500',
      'IN_PROGRESS': 'text-yellow-600',
      'PICKED': 'text-green-600'
    };
    return colors[status] || 'text-gray-500';
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </Layout>
    );
  }

  if (!pickList) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-gray-600">Pick list not found</p>
          <Button onClick={() => navigate('/warehouse/pick-lists')} className="mt-4">
            Back to Pick Lists
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="pick-list-view-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/warehouse/pick-lists')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ClipboardList className="w-7 h-7 text-amber-600" />
                {pickList.pick_list_number}
              </h1>
              <p className="text-sm text-gray-500">Wave: {pickList.wave_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(pickList.status)}>
              {pickList.status?.replace(/_/g, ' ')}
            </Badge>
            {pickList.status !== 'PICKED' && !isPickingMode && (
              <Button 
                onClick={() => setIsPickingMode(true)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Play className="w-4 h-4 mr-2" /> Start Picking
              </Button>
            )}
            {pickList.status === 'PICKED' && (
              <Button 
                onClick={() => navigate(`/warehouse/packing?pickList=${pickListNumber}`)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Package className="w-4 h-4 mr-2" /> Proceed to Packing
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Summary */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Pick List Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping Point:</span>
                  <span className="font-medium">{pickList.shipping_point}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Planned GI Date:</span>
                  <span className="font-medium">{pickList.planned_gi_date || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Items:</span>
                  <span className="font-medium">{pickList.total_items}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Deliveries:</span>
                  <span className="font-medium">{pickList.delivery_numbers?.length || 0}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Progress</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Completion</span>
                    <span className="font-medium">{pickList.pick_completion_percent?.toFixed(1) || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-amber-600 h-3 rounded-full transition-all"
                      style={{ width: `${pickList.pick_completion_percent || 0}%` }}
                    />
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Picked Quantity:</span>
                  <span className="font-medium">{pickList.picked_quantity || 0} / {pickList.total_quantity}</span>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Linked Deliveries</h3>
              <div className="space-y-2">
                {(pickList.delivery_numbers || []).map(dn => (
                  <div 
                    key={dn}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100"
                    onClick={() => navigate(`/otc/deliveries/${dn}`)}
                  >
                    <span className="font-medium text-sm">{dn}</span>
                    <ArrowLeft className="w-4 h-4 rotate-180 text-gray-400" />
                  </div>
                ))}
              </div>
            </Card>

            {isPickingMode && (
              <div className="space-y-2">
                <Button 
                  onClick={handleConfirmPicking}
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirming...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Confirm All Picks</>
                  )}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setIsPickingMode(false)}
                  className="w-full"
                >
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
              </div>
            )}
          </div>

          {/* Right Column - Items */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Pick Items</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Material</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-center">Requested</th>
                      {isPickingMode ? (
                        <th className="px-3 py-2 text-center">Pick Qty</th>
                      ) : (
                        <th className="px-3 py-2 text-center">Picked</th>
                      )}
                      <th className="px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(pickList.items || []).map(item => (
                      <tr key={item.item_number} className="hover:bg-gray-50">
                        <td className="px-3 py-3 font-medium">{item.item_number}</td>
                        <td className="px-3 py-3">
                          <div>
                            <p className="font-medium">{item.material_id}</p>
                            <p className="text-xs text-gray-500">{item.material_description}</p>
                            <p className="text-xs text-gray-400">Delivery: {item.delivery_number}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            <span>{item.storage_location || '-'}</span>
                          </div>
                          {item.storage_bin && (
                            <p className="text-xs text-gray-500">Bin: {item.storage_bin}</p>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {item.requested_quantity} {item.unit_of_measure}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {isPickingMode && item.status !== 'PICKED' ? (
                            <div className="flex flex-col items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                max={item.requested_quantity}
                                step="0.01"
                                value={pickConfirmations[item.item_number]?.picked_quantity || ''}
                                onChange={(e) => handleQuantityChange(item.item_number, e.target.value)}
                                className="w-20 text-center h-8"
                                placeholder="0"
                              />
                              {pickConfirmations[item.item_number]?.picked_quantity < item.requested_quantity && 
                               pickConfirmations[item.item_number]?.picked_quantity > 0 && (
                                <Input
                                  type="text"
                                  value={pickConfirmations[item.item_number]?.variance_reason || ''}
                                  onChange={(e) => handleVarianceChange(item.item_number, e.target.value)}
                                  className="w-full text-xs h-6"
                                  placeholder="Variance reason"
                                />
                              )}
                            </div>
                          ) : (
                            <span className={item.picked_quantity > 0 ? 'text-green-600 font-medium' : ''}>
                              {item.picked_quantity || 0} {item.unit_of_measure}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`flex items-center justify-center gap-1 ${getItemStatusColor(item.status)}`}>
                            {item.status === 'PICKED' ? (
                              <><CheckCircle2 className="w-4 h-4" /> Picked</>
                            ) : item.status === 'IN_PROGRESS' ? (
                              <><Clock className="w-4 h-4" /> In Progress</>
                            ) : (
                              <><Clock className="w-4 h-4" /> Pending</>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PickListView;
